import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import {
	Box,
	Title,
	Text,
	Button,
	Textarea,
	Badge,
	Group,
	Grid,
	Modal,
	Divider,
	Card,
	Collapse,
	Tabs,
	Anchor,
	Input,
	TextInput,
	Menu,
	ActionIcon,
	Loader,
} from '@mantine/core'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
	addCheckInNote,
	deleteCheckInNote,
	getCheckInById,
	searchCheckIns,
	updateCheckIn,
	updateCheckInNote,
} from '../services/actions/checkInActions'
import PurchasedItemsModal from '../components/PurchasedItemsModal'
import CheckInEmbeddedPages from '../components/CheckInEmbeddedPages'
import PurchasedItemCard from '../components/PurchasedItemCard'
import { toast } from '../services/utils/toast'
import { buildBookingPageUrl } from '../services/utils/bookingNumberLink'

const orderGroupMembers = (members, seedId) => {
	if (!Array.isArray(members) || members.length === 0) return []

	const normalizedSeedId = String(seedId)
	const seed = members.find((member) => String(member.id) === normalizedSeedId)
	const others = members.filter(
		(member) => String(member.id) !== normalizedSeedId,
	)

	return seed ? [seed, ...others] : members
}

const findFirstUncheckedId = (members) => {
	const unchecked = members.find((member) => !member.checkInStatus)
	return unchecked ? String(unchecked.id) : null
}

const getNextUncheckedTravelerId = (members, currentId) => {
	if (!Array.isArray(members) || members.length === 0) return null

	const uncheckedCount = members.filter(
		(member) => !member.checkInStatus,
	).length
	if (uncheckedCount === 0) return null

	const normalizedCurrentId = String(currentId)
	const startIndex = members.findIndex(
		(member) => String(member.id) === normalizedCurrentId,
	)

	if (startIndex === -1) {
		return findFirstUncheckedId(members)
	}

	for (let offset = 1; offset <= members.length; offset += 1) {
		const idx = (startIndex + offset) % members.length
		if (!members[idx].checkInStatus) {
			return String(members[idx].id)
		}
	}

	return null
}

const getCheckInCustomFields = (checkIn) => {
	const customFields = checkIn?.customFields
	if (
		!customFields ||
		typeof customFields !== 'object' ||
		Array.isArray(customFields)
	) {
		return {}
	}
	return customFields
}

const formatCustomFieldValue = (value) => {
	if (value === null || value === undefined) return 'N/A'
	if (typeof value === 'string') return value.trim() || 'N/A'
	if (typeof value === 'number' || typeof value === 'boolean') {
		return String(value)
	}

	try {
		return JSON.stringify(value) || 'N/A'
	} catch {
		return String(value)
	}
}

const getTravelerCompletionStatus = (member) => {
	if (!member?.checkInStatus) return 'pending'
	const anyUnissued = Array.isArray(member.purchasedItems)
		? member.purchasedItems.some((item) => !item.issued)
		: false
	return anyUnissued ? 'partial' : 'complete'
}

const getTravelerTabUnderlineColor = (member) => {
	const status = getTravelerCompletionStatus(member)

	if (status === 'complete') {
		return '#2b8a3e'
	}

	if (status === 'partial') {
		return '#e67700'
	}

	return '#c92a2a'
}

const getTrimmedValue = (value) =>
	typeof value === 'string' ? value.trim() : ''

const isTravelerPhoneValid = (value) => {
	const normalizedValue = getTrimmedValue(value)
	if (!normalizedValue) return false
	return isValidPhoneNumber(normalizedValue)
}

const COLLECT_FIELD_DEFINITIONS = [
	{
		key: 'travelerPhone',
		label: 'Phone',
		checkInProp: 'travelerPhone',
		placeholder: 'Enter phone number',
		validate: (value) => {
			const trimmedValue = getTrimmedValue(value)
			if (!trimmedValue) return 'Phone is required'
			if (!isTravelerPhoneValid(trimmedValue)) {
				return 'Enter a valid phone number'
			}
			return ''
		},
	},
	{
		key: 'accountName',
		label: 'Account Name',
		checkInProp: 'accountName',
		placeholder: 'Enter account name',
		validate: (value) =>
			getTrimmedValue(value) ? '' : 'Account Name is required',
	},
]

const COLLECT_FIELD_DEFINITION_MAP = Object.fromEntries(
	COLLECT_FIELD_DEFINITIONS.map((field) => [field.key, field]),
)

const ON_SITE_PURCHASE_SOURCES = new Set([
	'livehq_on_site_sale',
	'livehq_on_site_free_item',
	'livehq_on_site_manual_payment',
])

const formatPurchaseAmount = (item) => {
	if (String(item?.paymentStatus || '').toLowerCase() === 'free') return 'Free'

	const hasAmountCents =
		item?.paymentAmountCents !== null &&
		item?.paymentAmountCents !== undefined &&
		item?.paymentAmountCents !== ''
	const amountCents = Number(item?.paymentAmountCents)
	if (hasAmountCents && Number.isFinite(amountCents)) {
		return `$${(amountCents / 100).toFixed(2)}`
	}

	const rawAmount = String(item?.paymentAmount || '').trim()
	if (!rawAmount) return 'Not recorded'
	if (rawAmount.startsWith('$')) return rawAmount
	if (/^\d+(\.\d{1,2})?$/.test(rawAmount)) return `$${rawAmount}`
	return rawAmount
}

const formatPurchaseDateTime = (value) => {
	if (!value) return 'Not recorded'
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return 'Not recorded'
	return date.toLocaleString([], {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	})
}

const normalizeCollectFieldEntry = (value) => {
	const isRecord = value && typeof value === 'object' && !Array.isArray(value)
	const require = isRecord ? Boolean(value.require) : false
	const collect = isRecord ? Boolean(value.collect) || require : require
	return { collect, require }
}

const normalizeCollectFieldsConfig = (value) => {
	const source =
		value && typeof value === 'object' && !Array.isArray(value) ? value : {}
	const normalized = {}
	COLLECT_FIELD_DEFINITIONS.forEach((field) => {
		normalized[field.key] = normalizeCollectFieldEntry(source[field.key])
	})
	return normalized
}

const createDefaultMissingFieldFormValues = () => {
	const defaults = {}
	COLLECT_FIELD_DEFINITIONS.forEach((field) => {
		defaults[field.key] = ''
	})
	return defaults
}

const getCollectFieldDefinition = (fieldKey) =>
	COLLECT_FIELD_DEFINITION_MAP[fieldKey] || null

const getMissingFieldPrompt = ({ collectFieldsConfig, travelerRecord }) => {
	const fieldKeys = []
	const requiredFieldKeys = []

	COLLECT_FIELD_DEFINITIONS.forEach((field) => {
		const fieldSettings = collectFieldsConfig[field.key] || {
			collect: false,
			require: false,
		}
		const currentValue = getTrimmedValue(travelerRecord?.[field.checkInProp])

		if (!fieldSettings.collect || currentValue.length > 0) return

		fieldKeys.push(field.key)
		if (fieldSettings.require) {
			requiredFieldKeys.push(field.key)
		}
	})

	return {
		fieldKeys,
		requiredFieldKeys,
	}
}

const CheckInDetailPage = () => {
	const { id, experienceSlug, orgSlug } = useParams()
	const navigate = useNavigate()
	const location = useLocation()
	const experiencePath = `/${orgSlug}/experiences/${experienceSlug}`
	const checkInDetailPath = `${experiencePath}/checkIn/${id}`

	const currentOrg = useSelector((state) => state.org)
	const currentUser = useSelector((state) => state.user)
	const userPosition = useSelector((state) => state.experience.userPosition)
	const experienceDetails = useSelector(
		(state) => state.experience?.experienceDetails,
	)
	const isOrgAdmin = currentOrg?.orgRole === 'admin'
	const canOverrideCriticalWarning =
		userPosition?.level === 'owner' || isOrgAdmin
	const canAddItems =
		userPosition?.level === 'owner' ||
		isOrgAdmin ||
		experienceDetails?.allowStaffAddItems

	const [checkIn, setCheckIn] = useState(null)
	const [groupMembers, setGroupMembers] = useState([])
	const [activeCheckInId, setActiveCheckInId] = useState(null)
	const [slideDirection, setSlideDirection] = useState('forward')
	const [panelAnimationKey, setPanelAnimationKey] = useState(0)

	const [purchasedItems, setPurchasedItems] = useState([])
	const [showAddItemModal, setShowAddItemModal] = useState(false)
	const [showPurchasesModal, setShowPurchasesModal] = useState(false)
	const [showNotes, setShowNotes] = useState(false)
	const [isCheckedIn, setIsCheckedIn] = useState(false)
	const [showWarningModal, setShowWarningModal] = useState(false)
	const [showBackWarningModal, setShowBackWarningModal] = useState(false)
	const [canContinueBack, setCanContinueBack] = useState(false)
	const [showCriticalModal, setShowCriticalModal] = useState(false)
	const [showCheckInDetailsModal, setShowCheckInDetailsModal] = useState(false)
	const [showAddTravelerModal, setShowAddTravelerModal] = useState(false)
	const [showSignatureModal, setShowSignatureModal] = useState(false)
	const [showMissingFieldsModal, setShowMissingFieldsModal] = useState(false)
	const [missingFieldPrompt, setMissingFieldPrompt] = useState({
		checkInId: null,
		fieldKeys: [],
		requiredFieldKeys: [],
	})
	const [missingFieldFormValues, setMissingFieldFormValues] = useState(
		createDefaultMissingFieldFormValues(),
	)
	const [missingFieldFormErrors, setMissingFieldFormErrors] = useState({})
	const [isSavingMissingFields, setIsSavingMissingFields] = useState(false)
	const [isSavingSignature, setIsSavingSignature] = useState(false)
	const [isSavingNote, setIsSavingNote] = useState(false)
	const [isFinalizingCheckIn, setIsFinalizingCheckIn] = useState(false)
	const [signatureHasInput, setSignatureHasInput] = useState(false)
	const [checkInNotes, setCheckInNotes] = useState([])
	const [noteMode, setNoteMode] = useState(null)
	const [noteDraftText, setNoteDraftText] = useState('')
	const [showTravelerDetails, setShowTravelerDetails] = useState(false)
	const [groupLeaderName, setGroupLeaderName] = useState('')
	const [groupLeaderSignatureDetails, setGroupLeaderSignatureDetails] =
		useState(null)
	const [travelerSearchTerm, setTravelerSearchTerm] = useState('')
	const [travelerSearchResults, setTravelerSearchResults] = useState([])
	const [isTravelerSearchLoading, setIsTravelerSearchLoading] = useState(false)
	const [travelerSearchError, setTravelerSearchError] = useState('')
	const [addingTravelerId, setAddingTravelerId] = useState(null)
	const signatureCanvasRef = useRef(null)
	const isSignatureDrawingRef = useRef(false)
	const isFinalizingCheckInRef = useRef(false)
	const pendingSignatureActionRef = useRef(null)
	const previousActiveCheckInIdRef = useRef(null)
	const activeCheckInIdRef = useRef(null)
	const missingFieldPromptedTravelerIdRef = useRef(null)
	const checkInMutationErrorToastId = 'checkin-mutation-failed'
	const missingFieldsSaveSuccessToastId = 'missing-fields-save-success'
	const missingFieldsSaveErrorToastId = 'missing-fields-save-failed'

	const isGroupMode = checkIn?.checkInType === 'group'
	const activeCheckIn = isGroupMode
		? groupMembers.find(
				(member) => String(member.id) === String(activeCheckInId),
			) || null
		: checkIn
	const activeCustomFields = Object.entries(
		getCheckInCustomFields(activeCheckIn),
	)
	const bookingPageUrl = buildBookingPageUrl(
		experienceDetails?.bookingNumberLink,
		activeCheckIn?.bookingNumber,
	)
	const activeTravelerCheckInId = activeCheckIn?.id
		? String(activeCheckIn.id)
		: null
	const collectFieldsConfig = useMemo(
		() => normalizeCollectFieldsConfig(checkIn?.collectFields),
		[checkIn?.collectFields],
	)
	const seedTravelerName = checkIn?.travelerName || 'Traveler'
	const uncheckedGroupCount = isGroupMode
		? groupMembers.filter((member) => !member.checkInStatus).length
		: 0
	const shouldCollectSignature = Boolean(checkIn?.collectCheckInSignature)
	const signatureDisclaimerText = isGroupMode
		? checkIn?.signatureDisclaimerGroup
		: checkIn?.signatureDisclaimerIndividual
	const signaturePromptText =
		signatureDisclaimerText ||
		(isGroupMode
			? 'Please sign to complete this group check-in.'
			: 'Please sign to complete this check-in.')
	const checkInDetailsSignatureDetails =
		activeCheckIn?.signatureDetails ||
		(isGroupMode ? groupLeaderSignatureDetails : null)
	const checkInDetailsSignature =
		checkInDetailsSignatureDetails?.signature || null
	const checkInDetailsSignatureDateTime =
		checkInDetailsSignatureDetails?.signatureDateTime || null
	const checkInDetailsSignatureLabel = activeCheckIn?.signatureDetails
		?.signature
		? 'Traveler Signature'
		: isGroupMode && groupLeaderSignatureDetails?.signature
			? 'Group Leader Signature'
			: 'Signature'
	const hasRequiredMissingFieldPrompt =
		missingFieldPrompt.requiredFieldKeys.length > 0

	const hasNonEmptyGroupId = (value) =>
		typeof value === 'string' ? value.trim().length > 0 : value != null

	const createGroupId = () => {
		if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
			return window.crypto.randomUUID()
		}
		return `${Date.now()}-${Math.random().toString(16).slice(2)}`
	}

	const getTravelerAddBlockers = (traveler) => {
		const blockers = []
		if (!traveler) return ['Unavailable traveler']
		if (String(traveler.id) === String(activeCheckIn?.id)) {
			blockers.push('Current traveler')
		}
		if (traveler.checkInStatus) {
			blockers.push('Already checked in')
		}
		if (traveler.checkInType === 'group') {
			blockers.push('Already in a group')
		}
		return blockers
	}

	const syncActiveTravelerState = (traveler) => {
		setPurchasedItems(traveler?.purchasedItems || [])
		setCheckInNotes(traveler?.checkInNotes || [])
		setIsCheckedIn(traveler?.checkInStatus || false)
		setShowPurchasesModal(false)
		setShowNotes(
			Array.isArray(traveler?.checkInNotes) && traveler.checkInNotes.length > 0,
		)
		setShowTravelerDetails(false)
		setNoteMode(null)
		setNoteDraftText('')
	}

	const showCheckInMutationError = (err) => {
		const fallbackMessage = "We couldn't save this check-in. Please try again."
		const message =
			typeof err?.message === 'string' && err.message.trim().length > 0
				? err.message
				: fallbackMessage

		toast.error({
			id: checkInMutationErrorToastId,
			title: 'Check-in failed',
			message,
		})
	}

	const showMissingFieldsSaveSuccess = () => {
		toast.success({
			id: missingFieldsSaveSuccessToastId,
			title: 'Traveler updated',
			message: 'Missing fields were saved successfully.',
		})
	}

	const showMissingFieldsSaveError = (err) => {
		const fallbackMessage =
			"We couldn't save the missing traveler fields. Please try again."
		const message =
			typeof err?.message === 'string' && err.message.trim().length > 0
				? err.message
				: fallbackMessage

		toast.error({
			id: missingFieldsSaveErrorToastId,
			title: 'Unable to save fields',
			message,
		})
	}

	const fetchData = useCallback(
		async ({ preserveActive = true } = {}) => {
			try {
				const checkInData = await getCheckInById(orgSlug, id)
				setCheckIn(checkInData)

				if (checkInData?.checkInType === 'group') {
					const rawMembers =
						Array.isArray(checkInData.groupMembers) &&
						checkInData.groupMembers.length > 0
							? checkInData.groupMembers
							: [checkInData]
					const orderedMembers = orderGroupMembers(rawMembers, id)
					setGroupMembers(orderedMembers)

					const fallbackActiveId =
						findFirstUncheckedId(orderedMembers) ||
						String(id) ||
						(orderedMembers[0] ? String(orderedMembers[0].id) : null)

					setActiveCheckInId((prev) => {
						if (
							preserveActive &&
							prev &&
							orderedMembers.some(
								(member) => String(member.id) === String(prev),
							)
						) {
							return prev
						}
						return fallbackActiveId
					})

					return { checkInData, members: orderedMembers }
				}

				setGroupMembers([])
				setActiveCheckInId(checkInData ? String(checkInData.id) : null)
				return { checkInData, members: checkInData ? [checkInData] : [] }
			} catch (err) {
				console.error('❌ Failed to fetch:', err)
				navigate(experiencePath)
				return null
			}
		},
		[experiencePath, id, navigate, orgSlug],
	)

	const refreshActiveCheckInData = useCallback(
		() => fetchData({ preserveActive: true }),
		[fetchData],
	)

	useEffect(() => {
		fetchData({ preserveActive: false })
	}, [fetchData])

	useEffect(() => {
		if (!activeCheckIn) return
		syncActiveTravelerState(activeCheckIn)
	}, [activeCheckIn])

	useEffect(() => {
		if (!showBackWarningModal) return
		const timer = setTimeout(() => setCanContinueBack(true), 2000)
		return () => clearTimeout(timer)
	}, [showBackWarningModal])

	useEffect(() => {
		const normalizedActiveCheckInId = activeCheckInId
			? String(activeCheckInId)
			: null
		if (!normalizedActiveCheckInId) return

		if (
			activeCheckInIdRef.current &&
			activeCheckInIdRef.current !== normalizedActiveCheckInId
		) {
			previousActiveCheckInIdRef.current = activeCheckInIdRef.current
		}
		activeCheckInIdRef.current = normalizedActiveCheckInId
	}, [activeCheckInId])

	useEffect(() => {
		if (!activeTravelerCheckInId) return
		if (activeCheckIn?.checkInStatus) {
			setShowMissingFieldsModal(false)
			setMissingFieldPrompt({
				checkInId: activeTravelerCheckInId,
				fieldKeys: [],
				requiredFieldKeys: [],
			})
			setMissingFieldFormErrors({})
			setIsSavingMissingFields(false)
			return
		}
		if (missingFieldPromptedTravelerIdRef.current === activeTravelerCheckInId) {
			return
		}
		missingFieldPromptedTravelerIdRef.current = activeTravelerCheckInId

		const prompt = getMissingFieldPrompt({
			collectFieldsConfig,
			travelerRecord: activeCheckIn,
		})

		if (prompt.fieldKeys.length === 0) {
			setShowMissingFieldsModal(false)
			setMissingFieldPrompt({
				checkInId: activeTravelerCheckInId,
				fieldKeys: [],
				requiredFieldKeys: [],
			})
			setMissingFieldFormErrors({})
			setIsSavingMissingFields(false)
			return
		}

		setMissingFieldPrompt({
			checkInId: activeTravelerCheckInId,
			fieldKeys: prompt.fieldKeys,
			requiredFieldKeys: prompt.requiredFieldKeys,
		})
		setMissingFieldFormValues(() => {
			const nextValues = createDefaultMissingFieldFormValues()
			COLLECT_FIELD_DEFINITIONS.forEach((field) => {
				nextValues[field.key] = activeCheckIn?.[field.checkInProp] || ''
			})
			return nextValues
		})
		setMissingFieldFormErrors({})
		setIsSavingMissingFields(false)
		setShowMissingFieldsModal(true)
	}, [
		activeTravelerCheckInId,
		activeCheckIn,
		activeCheckIn?.checkInStatus,
		collectFieldsConfig,
	])

	useEffect(() => {
		let isMounted = true
		const leaderId = activeCheckIn?.groupLeaderId

		if (!isGroupMode || leaderId == null) {
			setGroupLeaderName('')
			setGroupLeaderSignatureDetails(null)
			return
		}

		if (String(leaderId) === String(activeCheckIn?.id)) {
			setGroupLeaderName(activeCheckIn?.travelerName || 'N/A')
			setGroupLeaderSignatureDetails(activeCheckIn?.signatureDetails || null)
			return
		}

		if (isGroupMode) {
			const leaderFromGroup = groupMembers.find(
				(member) => String(member.id) === String(leaderId),
			)
			if (leaderFromGroup) {
				setGroupLeaderName(leaderFromGroup.travelerName || 'N/A')
				setGroupLeaderSignatureDetails(leaderFromGroup.signatureDetails || null)
				return
			}
		}

		const fetchGroupLeader = async () => {
			try {
				const leaderCheckIn = await getCheckInById(orgSlug, leaderId)
				if (isMounted) {
					setGroupLeaderName(leaderCheckIn?.travelerName || 'N/A')
					setGroupLeaderSignatureDetails(
						leaderCheckIn?.signatureDetails || null,
					)
				}
			} catch (err) {
				console.error('❌ Failed to fetch group leader:', err)
				if (isMounted) {
					setGroupLeaderName('N/A')
					setGroupLeaderSignatureDetails(null)
				}
			}
		}

		fetchGroupLeader()

		return () => {
			isMounted = false
		}
	}, [
		activeCheckIn?.groupLeaderId,
		activeCheckIn?.id,
		activeCheckIn?.signatureDetails,
		activeCheckIn?.travelerName,
		groupMembers,
		isGroupMode,
		orgSlug,
	])

	useEffect(() => {
		if (!showAddTravelerModal) {
			setTravelerSearchTerm('')
			setTravelerSearchResults([])
			setTravelerSearchError('')
			setIsTravelerSearchLoading(false)
			return
		}

		const query = travelerSearchTerm.trim()
		if (query.length < 3) {
			setTravelerSearchResults([])
			setTravelerSearchError('')
			setIsTravelerSearchLoading(false)
			return
		}

		let isMounted = true
		const timer = setTimeout(async () => {
			setIsTravelerSearchLoading(true)
			setTravelerSearchError('')
			try {
				const results = await searchCheckIns(experienceSlug, orgSlug, query)
				if (!isMounted) return
				setTravelerSearchResults(Array.isArray(results) ? results : [])
			} catch (err) {
				console.error('❌ Failed to search travelers:', err)
				if (isMounted) {
					setTravelerSearchResults([])
					setTravelerSearchError('Unable to search travelers right now')
				}
			} finally {
				if (isMounted) {
					setIsTravelerSearchLoading(false)
				}
			}
		}, 250)

		return () => {
			isMounted = false
			clearTimeout(timer)
		}
	}, [experienceSlug, orgSlug, showAddTravelerModal, travelerSearchTerm])

	const handleAddTravelerToGroup = async (traveler) => {
		if (!checkIn || !traveler) return

		const blockers = getTravelerAddBlockers(traveler)
		if (blockers.length > 0) return

		setAddingTravelerId(traveler.id)
		setTravelerSearchError('')
		setTravelerSearchTerm('')
		setTravelerSearchResults([])
		try {
			let targetGroupId = hasNonEmptyGroupId(checkIn.groupId)
				? checkIn.groupId
				: null

			if (!targetGroupId) {
				targetGroupId = createGroupId()
				await updateCheckIn(orgSlug, id, { groupId: targetGroupId })
			}

			await updateCheckIn(orgSlug, traveler.id, { groupId: targetGroupId })

			await fetchData({ preserveActive: true })
			toast.success({
				title: 'Traveler added',
				message: `${traveler.travelerName || 'Traveler'} was added to the group.`,
			})
		} catch (err) {
			console.error('❌ Failed to add traveler to group:', err)
			setTravelerSearchError('Failed to add traveler to this group')
			showCheckInMutationError(err)
		} finally {
			setAddingTravelerId(null)
		}
	}

	const clearSignatureCanvas = () => {
		const canvas = signatureCanvasRef.current
		if (!canvas) return
		const ctx = canvas.getContext('2d')
		if (!ctx) return

		ctx.fillStyle = '#ffffff'
		ctx.fillRect(0, 0, canvas.width, canvas.height)
		setSignatureHasInput(false)
	}

	useEffect(() => {
		if (!showSignatureModal) return
		const timer = setTimeout(() => {
			clearSignatureCanvas()
		}, 0)
		return () => clearTimeout(timer)
	}, [showSignatureModal])

	const getSignaturePoint = (event) => {
		const canvas = signatureCanvasRef.current
		if (!canvas) return { x: 0, y: 0 }
		const rect = canvas.getBoundingClientRect()
		const scaleX = canvas.width / rect.width
		const scaleY = canvas.height / rect.height
		return {
			x: (event.clientX - rect.left) * scaleX,
			y: (event.clientY - rect.top) * scaleY,
		}
	}

	const handleSignaturePointerDown = (event) => {
		if (isSavingSignature) return
		event.preventDefault()

		const canvas = signatureCanvasRef.current
		if (!canvas) return
		const ctx = canvas.getContext('2d')
		if (!ctx) return

		const point = getSignaturePoint(event)
		ctx.strokeStyle = '#111827'
		ctx.lineWidth = 2.5
		ctx.lineCap = 'round'
		ctx.lineJoin = 'round'
		ctx.beginPath()
		ctx.moveTo(point.x, point.y)
		isSignatureDrawingRef.current = true
		setSignatureHasInput(true)
	}

	const handleSignaturePointerMove = (event) => {
		if (!isSignatureDrawingRef.current || isSavingSignature) return
		event.preventDefault()

		const canvas = signatureCanvasRef.current
		if (!canvas) return
		const ctx = canvas.getContext('2d')
		if (!ctx) return

		const point = getSignaturePoint(event)
		ctx.lineTo(point.x, point.y)
		ctx.stroke()
	}

	const handleSignaturePointerUp = () => {
		isSignatureDrawingRef.current = false
	}

	const closeSignatureModal = () => {
		isSignatureDrawingRef.current = false
		pendingSignatureActionRef.current = null
		setShowSignatureModal(false)
		setIsSavingSignature(false)
		setSignatureHasInput(false)
		isFinalizingCheckInRef.current = false
		setIsFinalizingCheckIn(false)
	}

	const releaseFinalizeLock = () => {
		isFinalizingCheckInRef.current = false
		setIsFinalizingCheckIn(false)
	}

	const runFinalizeWithLock = async (action) => {
		if (isFinalizingCheckInRef.current) return
		isFinalizingCheckInRef.current = true
		setIsFinalizingCheckIn(true)

		try {
			await action()
		} finally {
			if (!pendingSignatureActionRef.current) {
				releaseFinalizeLock()
			}
		}
	}

	const runWithRequiredSignature = async ({
		signatureTargetCheckInId,
		onSignatureConfirmedAction,
	}) => {
		if (!shouldCollectSignature) {
			await onSignatureConfirmedAction()
			return
		}

		pendingSignatureActionRef.current = {
			signatureTargetCheckInId,
			onSignatureConfirmedAction,
		}
		setShowSignatureModal(true)
	}

	const handleConfirmSignature = async () => {
		if (!signatureHasInput || isSavingSignature) return
		const pending = pendingSignatureActionRef.current
		const canvas = signatureCanvasRef.current
		if (!pending || !canvas) return

		setIsSavingSignature(true)
		try {
			const signatureDataUrl = canvas.toDataURL('image/png')

			await updateCheckIn(orgSlug, pending.signatureTargetCheckInId, {
				signatureDetails: {
					signature: signatureDataUrl,
					signatureDateTime: new Date().toISOString(),
					signatureTerms: signaturePromptText || null,
				},
			})

			setShowSignatureModal(false)
			pendingSignatureActionRef.current = null
			isSignatureDrawingRef.current = false
			setSignatureHasInput(false)

			await pending.onSignatureConfirmedAction()
		} catch (err) {
			console.error('❌ Failed to capture signature:', err)
			showCheckInMutationError(err)
		} finally {
			setIsSavingSignature(false)
			releaseFinalizeLock()
		}
	}

	const buildPatchPayload = ({ targetCheckIn, status, itemList }) => {
		const payload = {
			travelerName: targetCheckIn?.travelerName,
			travelerEmail: targetCheckIn?.travelerEmail,
			checkInStatus: status,
			purchasedItems: (itemList || []).map((item) => ({
				id: item.id,
				bookingItemId: item.bookingItemId,
				bookingId: item.bookingId,
				orderId: item.orderId,
				transactionId: item.transactionId,
				source:
					item.source ||
					(item.bookingItemId ? 'livehq_on_site_sale' : 'manual'),
				paymentStatus: item.paymentStatus,
				issued: item.issued,
				addedByUser: item.addedByUser,
				paymentMethod: item.paymentMethod,
				paymentAmount: item.paymentAmount,
				paymentAmountCents: item.paymentAmountCents,
			})),
			...(isGroupMode && status === true ? { groupLeaderId: id } : {}),
		}

		return payload
	}

	const patchCheckIn = async ({
		targetCheckIn,
		status,
		itemList = purchasedItems,
	}) => {
		if (!targetCheckIn) return null

		const payload = buildPatchPayload({
			targetCheckIn,
			status,
			itemList,
		})

		await updateCheckIn(orgSlug, targetCheckIn.id, payload)
		return fetchData({ preserveActive: true })
	}

	const setActiveTravelerWithDirection = (nextId) => {
		if (!nextId || !isGroupMode) return

		const normalizedNextId = String(nextId)
		const currentIndex = groupMembers.findIndex(
			(member) => String(member.id) === String(activeCheckInId),
		)
		const nextIndex = groupMembers.findIndex(
			(member) => String(member.id) === normalizedNextId,
		)

		if (nextIndex === -1) return

		setSlideDirection(nextIndex >= currentIndex ? 'forward' : 'backward')
		setPanelAnimationKey((prev) => prev + 1)
		setActiveCheckInId(normalizedNextId)
	}

	const hasCriticalWarning = activeCheckIn?.warnings?.some(
		(warning) => warning.level === 'Critical',
	)

	const finalizeIndividualCheckIn = async ({
		targetCheckIn = activeCheckIn,
	} = {}) => {
		try {
			const result = await patchCheckIn({
				targetCheckIn,
				status: true,
			})

			const refreshedName =
				result?.checkInData?.travelerName ||
				targetCheckIn?.travelerName ||
				'Traveler'
			const notificationId = `checkin-success-${id}`
			toast.success({
				id: notificationId,
				title: 'Traveler checked in',
				autoClose: 10000,
				message: (
					<Group spacing='xs' position='apart' noWrap>
						<Text size='sm'>{refreshedName} is checked in.</Text>
						<Button
							size='xs'
							variant='light'
							onClick={() => {
								toast.hide(notificationId)
								navigate(checkInDetailPath)
							}}
						>
							Go back
						</Button>
					</Group>
				),
			})
			navigate(experiencePath)
		} catch (err) {
			console.error('❌ Failed to finalize check-in:', err)
			showCheckInMutationError(err)
		}
	}

	const finalizeGroupCheckIn = () => {
		const notificationId = `group-checkin-success-${id}`
		toast.success({
			id: notificationId,
			title: 'Group checked in',
			autoClose: 10000,
			message: (
				<Group spacing='xs' position='apart' noWrap>
					<Text size='sm'>
						Successfully checked in {seedTravelerName}&apos;s group
					</Text>
					<Button
						size='xs'
						variant='light'
						onClick={() => {
							toast.hide(notificationId)
							navigate(checkInDetailPath)
						}}
					>
						Go back
					</Button>
				</Group>
			),
		})
		navigate(experiencePath)
	}

	const handleIndividualPrimaryAction = async ({
		pendingNoteText = '',
	} = {}) => {
		if (!activeCheckIn) return
		const targetCheckIn = activeCheckIn
		try {
			await runWithRequiredSignature({
				signatureTargetCheckInId: targetCheckIn.id,
				onSignatureConfirmedAction: async () => {
					await savePendingNoteForCheckIn(targetCheckIn, pendingNoteText)
					await finalizeIndividualCheckIn({ targetCheckIn })
				},
			})
		} catch (err) {
			console.error('❌ Failed to run check-in action:', err)
			showCheckInMutationError(err)
		}
	}

	const handleGroupPrimaryAction = async ({ pendingNoteText = '' } = {}) => {
		try {
			if (!activeCheckIn) return
			const targetCheckIn = activeCheckIn

			if (isCheckedIn) {
				if (uncheckedGroupCount > 0) {
					const nextUncheckedId = getNextUncheckedTravelerId(
						groupMembers,
						activeCheckIn.id,
					)
					if (nextUncheckedId) {
						setActiveTravelerWithDirection(nextUncheckedId)
						return
					}
				}
				await runWithRequiredSignature({
					signatureTargetCheckInId: id,
					onSignatureConfirmedAction: async () => {
						finalizeGroupCheckIn()
					},
				})
				return
			}

			const beforeUncheckedCount = uncheckedGroupCount
			const wasLastUncheckedTraveler = beforeUncheckedCount <= 1
			const currentActiveId = String(targetCheckIn.id)

			if (wasLastUncheckedTraveler) {
				await runWithRequiredSignature({
					signatureTargetCheckInId: id,
					onSignatureConfirmedAction: async () => {
						await savePendingNoteForCheckIn(targetCheckIn, pendingNoteText)
						await patchCheckIn({
							targetCheckIn,
							status: true,
						})
						finalizeGroupCheckIn()
					},
				})
				return
			}

			await savePendingNoteForCheckIn(targetCheckIn, pendingNoteText)
			const result = await patchCheckIn({
				targetCheckIn,
				status: true,
			})

			const refreshedMembers = result?.members || []
			const nextUncheckedId = getNextUncheckedTravelerId(
				refreshedMembers,
				currentActiveId,
			)
			if (nextUncheckedId) {
				setActiveTravelerWithDirection(nextUncheckedId)
			}
		} catch (err) {
			console.error('❌ Failed to process group check-in action:', err)
			showCheckInMutationError(err)
		}
	}

	const handleAttemptPrimaryAction = () => {
		if (isFinalizingCheckInRef.current) return

		if (!isCheckedIn && hasCriticalWarning && !canOverrideCriticalWarning) {
			setShowCriticalModal(true)
			return
		}

		if (isGroupMode && isCheckedIn) {
			runFinalizeWithLock(() => handleGroupPrimaryAction())
			return
		}

		const anyUnissued = purchasedItems.some((item) => !item.issued)
		if (anyUnissued) {
			setShowWarningModal(true)
			return
		}

		if (isGroupMode) {
			runFinalizeWithLock(() => handleGroupPrimaryAction())
			return
		}

		runFinalizeWithLock(() => handleIndividualPrimaryAction())
	}

	const handleUndoCheckIn = async () => {
		if (!activeCheckIn) return
		try {
			await patchCheckIn({
				targetCheckIn: activeCheckIn,
				status: false,
			})
		} catch (err) {
			console.error('❌ Failed to undo check-in:', err)
			showCheckInMutationError(err)
		}
	}

	const toggleItem = async (index) => {
		if (isCheckedIn || !activeCheckIn) return

		const updated = [...purchasedItems]
		updated[index] = {
			...updated[index],
			issued: !updated[index].issued,
		}
		setPurchasedItems(updated)

		try {
			await patchCheckIn({
				targetCheckIn: activeCheckIn,
				status: isCheckedIn,
				itemList: updated,
			})
		} catch (err) {
			console.error('❌ Failed to toggle item:', err)
			showCheckInMutationError(err)
		}
	}

	const toggleNotes = () => {
		setShowNotes((prev) => !prev)
		setNoteMode(checkInNotes?.length === 0 && 'add')
	}

	const handleBack = () => {
		const hasUnfinalizedIssuedItems =
			(!isCheckedIn && purchasedItems.some((item) => item.issued)) ||
			(isGroupMode &&
				groupMembers.some(
					(member) =>
						String(member.id) !== String(activeCheckInId) &&
						!member.checkInStatus &&
						member.purchasedItems?.some((item) => item.issued),
				))

		if (hasUnfinalizedIssuedItems) {
			setCanContinueBack(false)
			setShowBackWarningModal(true)
			return
		}

		navigate(experiencePath)
	}

	const getNoteId = (note) =>
		typeof note?.id === 'string' ? note.id.trim() : ''

	const savePendingNoteForCheckIn = async (targetCheckIn, text) => {
		const trimmed = typeof text === 'string' ? text.trim() : ''
		if (!targetCheckIn || !trimmed) return null
		return addCheckInNote(orgSlug, targetCheckIn.id, trimmed)
	}

	const handleSaveNote = async () => {
		if (!activeCheckIn || isSavingNote) return

		const trimmed = noteDraftText.trim()
		if (!trimmed) return

		try {
			setIsSavingNote(true)
			if (noteMode === 'add') {
				await addCheckInNote(orgSlug, activeCheckIn.id, trimmed)
			} else if (typeof noteMode === 'number') {
				const noteId = getNoteId(checkInNotes[noteMode])
				if (!noteId) {
					throw new Error('This note needs to be refreshed before editing.')
				}
				await updateCheckInNote(orgSlug, activeCheckIn.id, noteId, trimmed)
			}
			setNoteMode(null)
			setNoteDraftText('')
			await fetchData({ preserveActive: true })
		} catch (err) {
			console.error('❌ Failed to save note:', err)
			showCheckInMutationError(err)
		} finally {
			setIsSavingNote(false)
		}
	}

	const handleCancelNote = () => {
		setNoteMode(null)
		setNoteDraftText('')
	}

	const handleDeleteNote = async (idx) => {
		if (!activeCheckIn || isSavingNote) return

		const confirmed = window.confirm('Delete this note?')
		if (!confirmed) return

		try {
			const noteId = getNoteId(checkInNotes[idx])
			if (!noteId) {
				throw new Error('This note needs to be refreshed before deleting.')
			}
			setIsSavingNote(true)
			await deleteCheckInNote(orgSlug, activeCheckIn.id, noteId)
			if (noteMode === idx) {
				setNoteMode(null)
				setNoteDraftText('')
			}
			await fetchData({ preserveActive: true })
		} catch (err) {
			console.error('❌ Failed to delete note:', err)
			showCheckInMutationError(err)
		} finally {
			setIsSavingNote(false)
		}
	}

	const formatCheckInTime = (isoString) => {
		const date = new Date(isoString)
		return date.toLocaleTimeString([], {
			hour: 'numeric',
			minute: '2-digit',
		})
	}

	const formatActualCheckInDateTime = (isoString) => {
		const date = new Date(isoString)
		return date.toLocaleString([], {
			month: '2-digit',
			day: '2-digit',
			year: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
		})
	}

	const formatSignatureDateTime = (isoString) => {
		if (!isoString) return null
		const date = new Date(isoString)
		if (Number.isNaN(date.getTime())) return null
		return date.toLocaleString([], {
			month: 'long',
			day: 'numeric',
			year: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
		})
	}

	const getMissingFieldValueError = (fieldKey, value) => {
		const fieldDefinition = getCollectFieldDefinition(fieldKey)
		if (!fieldDefinition) return ''
		const isRequiredField =
			missingFieldPrompt.requiredFieldKeys.includes(fieldKey)
		const trimmedValue = getTrimmedValue(value)

		// Optional collected fields should not block Done when left blank.
		if (!isRequiredField && trimmedValue.length === 0) {
			return ''
		}

		return fieldDefinition.validate(value)
	}

	const updateMissingFieldValue = (fieldKey, value) => {
		setMissingFieldFormValues((prev) => ({
			...prev,
			[fieldKey]: value,
		}))

		if (!missingFieldFormErrors[fieldKey]) return
		setMissingFieldFormErrors((prev) => ({
			...prev,
			[fieldKey]: getMissingFieldValueError(fieldKey, value),
		}))
	}

	const validateMissingFieldForm = () => {
		const nextErrors = {}
		missingFieldPrompt.fieldKeys.forEach((fieldKey) => {
			nextErrors[fieldKey] = getMissingFieldValueError(
				fieldKey,
				missingFieldFormValues[fieldKey],
			)
		})

		setMissingFieldFormErrors(nextErrors)
		return Object.values(nextErrors).every((message) => !message)
	}

	const hasAnyMissingFieldInput = missingFieldPrompt.fieldKeys.some(
		(fieldKey) => getTrimmedValue(missingFieldFormValues[fieldKey]).length > 0,
	)

	const isMissingFieldDoneEnabled =
		missingFieldPrompt.fieldKeys.length > 0 &&
		hasAnyMissingFieldInput &&
		missingFieldPrompt.fieldKeys.every(
			(fieldKey) =>
				!getMissingFieldValueError(fieldKey, missingFieldFormValues[fieldKey]),
		)

	const handleMissingFieldsBack = () => {
		if (isSavingMissingFields) return

		setShowMissingFieldsModal(false)
		setMissingFieldFormErrors({})
		setMissingFieldPrompt((prev) => ({
			...prev,
			fieldKeys: [],
			requiredFieldKeys: [],
		}))

		const previousTravelerId = previousActiveCheckInIdRef.current
		if (
			isGroupMode &&
			previousTravelerId &&
			String(previousTravelerId) !== String(activeCheckIn?.id) &&
			groupMembers.some(
				(member) => String(member.id) === String(previousTravelerId),
			)
		) {
			setActiveTravelerWithDirection(previousTravelerId)
			return
		}

		navigate(experiencePath)
	}

	const handleMissingFieldsSkip = () => {
		if (isSavingMissingFields) return
		setShowMissingFieldsModal(false)
		setMissingFieldFormErrors({})
	}

	const handleMissingFieldsDone = async () => {
		if (isSavingMissingFields || !activeCheckIn) return

		if (!validateMissingFieldForm()) return

		const payload = {}
		missingFieldPrompt.fieldKeys.forEach((fieldKey) => {
			const fieldDefinition = getCollectFieldDefinition(fieldKey)
			if (!fieldDefinition) return
			const trimmedValue = getTrimmedValue(missingFieldFormValues[fieldKey])
			payload[fieldDefinition.checkInProp] = trimmedValue
		})

		try {
			setIsSavingMissingFields(true)
			await updateCheckIn(orgSlug, activeCheckIn.id, payload)
			setShowMissingFieldsModal(false)
			setMissingFieldFormErrors({})
			showMissingFieldsSaveSuccess()
			await fetchData({ preserveActive: true })
		} catch (err) {
			console.error('❌ Failed to save missing traveler fields:', err)
			showMissingFieldsSaveError(err)
		} finally {
			setIsSavingMissingFields(false)
		}
	}

	const handleMissingFieldsSubmit = (event) => {
		event.preventDefault()
		if (!isMissingFieldDoneEnabled || isSavingMissingFields) return
		handleMissingFieldsDone()
	}

	const primaryActionLabel = (() => {
		if (!isGroupMode) return 'Finalize Check-In'
		if (isCheckedIn) {
			return uncheckedGroupCount > 0 ? 'Next Traveler' : 'Finalize Check-In'
		}
		return uncheckedGroupCount > 1 ? 'Next Traveler' : 'Finalize Check-In'
	})()

	const warningConfirmLabel = (() => {
		if (!isGroupMode) return 'Yes, Check In Traveler'
		if (uncheckedGroupCount > 1) return 'Yes, Next Traveler'
		return 'Yes, Finalize Check-In'
	})()

	const handleGroupTabChange = (nextValue) => {
		if (!nextValue || !isGroupMode) return
		setActiveTravelerWithDirection(nextValue)
	}

	const handleViewMessageHistory = () => {
		if (!activeTravelerCheckInId) return
		navigate(`${experiencePath}/checkIn/${activeTravelerCheckInId}/messages`, {
			state: {
				returnTo: `${location.pathname}${location.search}${location.hash}`,
			},
		})
	}

	const onSitePurchasedItems = purchasedItems.filter((item) =>
		ON_SITE_PURCHASE_SOURCES.has(String(item?.source || '').trim()),
	)
	const getOnSitePurchaseSaleId = (item, fallbackIndex) => {
		const bookingItemId = String(item?.bookingItemId || '').trim()
		if (bookingItemId) return bookingItemId

		const transactionId = String(item?.transactionId || '').trim()
		if (transactionId) return transactionId

		const itemOptionId = String(
			item?.id || item?.itemOptionId || item?.itemOption?.id || 'item',
		).trim()
		const originalIndex = purchasedItems.indexOf(item)
		const itemIndex = originalIndex >= 0 ? originalIndex : fallbackIndex
		return `${activeCheckIn.id}:${itemIndex}:${itemOptionId || 'item'}`
	}
	const openOnSitePurchaseDetail = (item, index) => {
		const saleId = getOnSitePurchaseSaleId(item, index)
		navigate(
			`/${orgSlug}/experiences/${experienceSlug}/overview/sales/${encodeURIComponent(
				saleId,
			)}`,
		)
	}

	if (!checkIn || !currentUser || !activeCheckIn) return <Loader mt='xl' />

	return (
		<Box px='md' py='md'>
			<style>{`
				@keyframes traveler-panel-in-from-right {
					0% { transform: translateX(28px); opacity: 0.45; }
					100% { transform: translateX(0); opacity: 1; }
				}
				@keyframes traveler-panel-in-from-left {
					0% { transform: translateX(-28px); opacity: 0.45; }
					100% { transform: translateX(0); opacity: 1; }
				}
				@keyframes checkin-back-countdown {
					from { stroke-dashoffset: 100; }
					to { stroke-dashoffset: 0; }
				}
			`}</style>

			<Title order={2} mb='xs'>
				{isGroupMode
					? `${seedTravelerName}'s Group Check-In`
					: `${activeCheckIn.travelerName}'s Check-In`}
			</Title>

			{isGroupMode && (
				<Tabs
					value={String(activeCheckInId)}
					onTabChange={handleGroupTabChange}
					mt='sm'
					mb='md'
					styles={{
						list: {
							borderBottom: '1px solid #ced4da',
						},
						tab: {
							border: 'none',
							borderBottom: '3px solid transparent',
							borderRadius: 0,
							backgroundColor: 'transparent',
							paddingTop: '0.65rem',
							paddingBottom: '0.65rem',
						},
						tabLabel: {
							fontWeight: 500,
						},
					}}
				>
					<Tabs.List>
						{groupMembers.map((member) => {
							const isActive = String(member.id) === String(activeCheckInId)
							const underlineColor = getTravelerTabUnderlineColor(member)
							return (
								<Tabs.Tab
									key={member.id}
									value={String(member.id)}
									style={{
										borderBottomColor: isActive
											? underlineColor
											: 'transparent',
										fontWeight: isActive ? 700 : 500,
										color: '#1f2937',
									}}
								>
									{member.travelerName}
								</Tabs.Tab>
							)
						})}
					</Tabs.List>
				</Tabs>
			)}

			<Box
				key={`${activeCheckInId}-${panelAnimationKey}`}
				style={{
					animation:
						slideDirection === 'backward'
							? 'traveler-panel-in-from-left 240ms ease'
							: 'traveler-panel-in-from-right 240ms ease',
				}}
			>
				{Array.isArray(activeCheckIn.warnings) &&
					activeCheckIn.warnings.length > 0 && (
						<Group spacing='xs' mt='sm' mb='md'>
							{activeCheckIn.warnings.map((warning, idx) => (
								<Badge
									key={idx}
									color={
										warning.level === 'Critical'
											? 'red'
											: warning.level === 'Warning'
												? 'orange'
												: 'blue'
									}
									variant='filled'
									size='md'
								>
									{warning.title}
								</Badge>
							))}
						</Group>
					)}

				<Card withBorder radius='md' mt='sm' p='md'>
					{isGroupMode && activeCheckIn.groupLeaderId != null && (
						<Text>
							<strong>Group Leader:</strong> {groupLeaderName || 'N/A'}
						</Text>
					)}
					{isGroupMode && activeCheckIn.groupId != null && (
						<Text>
							<strong>Group:</strong> {activeCheckIn.groupId}
						</Text>
					)}
					<Text>
						<strong>Name:</strong> {activeCheckIn.travelerName || 'N/A'}
					</Text>
					<Text>
						<strong>Email:</strong> {activeCheckIn.travelerEmail || 'N/A'}
					</Text>
					<Text>
						<strong>Phone:</strong> {activeCheckIn.travelerPhone || 'N/A'}
					</Text>

					<Collapse in={showTravelerDetails}>
						<Box mt='sm'>
							<Text>
								<strong>Account:</strong> {activeCheckIn.accountName || 'N/A'}
							</Text>
							<Text>
								<strong>Station:</strong>{' '}
								{activeCheckIn?.station?.name || 'N/A'}
							</Text>
							{activeCheckIn?.segment?.name && (
								<Text>
									<strong>Segment:</strong> {activeCheckIn.segment.name}
								</Text>
							)}
							<Text>
								<strong>Scheduled Check-In Date:</strong>{' '}
								{activeCheckIn.scheduledCheckInDate
									? new Date(
											activeCheckIn.scheduledCheckInDate,
										).toLocaleDateString([], {
											month: 'long',
											day: 'numeric',
											year: 'numeric',
										})
									: 'N/A'}
							</Text>
							<Text>
								<strong>Booking Number:</strong>{' '}
								{activeCheckIn.bookingNumber || 'N/A'}
							</Text>
							{activeCustomFields.length > 0 && (
								<Box mt='sm'>
									{activeCustomFields.map(([key, value]) => (
										<Text key={key}>
											<strong>{key}:</strong>{' '}
											{formatCustomFieldValue(value)}
										</Text>
									))}
								</Box>
							)}
						</Box>
					</Collapse>

					<Button
						variant='subtle'
						size='xs'
						mt='sm'
						px={0}
						onClick={() => setShowTravelerDetails((prev) => !prev)}
					>
						{showTravelerDetails ? 'Hide details' : 'Show more details'}
					</Button>
				</Card>

				{isCheckedIn && activeCheckIn.actualCheckInDateTime && (
					<Badge
						color='green'
						variant='filled'
						size='lg'
						mt='xs'
						styles={{
							root: {
								padding: '0.35rem 0.65rem',
								textTransform: 'none',
							},
						}}
					>
						<Group spacing='xs' noWrap>
							<Text size='sm' fw={600} color='white'>
								Checked in at{' '}
								{formatCheckInTime(activeCheckIn.actualCheckInDateTime)}
							</Text>
							<Anchor
								size='sm'
								onClick={(event) => {
									event.preventDefault()
									setShowCheckInDetailsModal(true)
								}}
								style={{
									color: '#ffffff',
									textDecorationColor: '#ffffff',
									fontWeight: 600,
								}}
							>
								View details
							</Anchor>
						</Group>
					</Badge>
				)}

				<Group align='center' spacing='xs' mt='md'>
					<Title order={4}>Check-In Items</Title>
					{canAddItems && (
						<Button
							size='xs'
							ml='xs'
							onClick={() => setShowAddItemModal(true)}
							disabled={hasCriticalWarning && !canOverrideCriticalWarning}
							variant='outline'
						>
							+ Add
						</Button>
					)}
					{onSitePurchasedItems.length > 0 && (
						<Button
							size='xs'
							variant='light'
							onClick={() => setShowPurchasesModal(true)}
						>
							View purchases
						</Button>
					)}
				</Group>
				<Grid mt='sm'>
					{purchasedItems.map((item, idx) => (
						<Grid.Col key={idx} xs={12} sm={6} md={4}>
							<PurchasedItemCard
								name={item.name}
								imageUrl={item.imageUrl}
								description={item.description}
								issued={item.issued}
								disabled={
									isCheckedIn ||
									(hasCriticalWarning && !canOverrideCriticalWarning)
								}
								onToggle={() => toggleItem(idx)}
							/>
						</Grid.Col>
					))}
				</Grid>

				<Group mt='lg'>
					{!isGroupMode &&
						(isCheckedIn ? (
							<Button color='red' onClick={handleUndoCheckIn}>
								Undo Check-In
							</Button>
						) : (
							<Button
								onClick={handleAttemptPrimaryAction}
								loading={isFinalizingCheckIn}
								disabled={isFinalizingCheckIn}
							>
								Finalize Check-In
							</Button>
						))}

					{isGroupMode && (
						<>
							{uncheckedGroupCount > 0 && (
								<Button
									onClick={handleAttemptPrimaryAction}
									loading={isFinalizingCheckIn}
									disabled={isFinalizingCheckIn}
								>
									{primaryActionLabel}
								</Button>
							)}
							{isCheckedIn && (
								<Button color='red' onClick={handleUndoCheckIn}>
									Undo Check-In
								</Button>
							)}
						</>
					)}

					<Button variant='light' onClick={handleBack}>
						Back
					</Button>
					<CheckInEmbeddedPages
						key={activeTravelerCheckInId}
						checkInId={activeTravelerCheckInId}
					>
						{(embeddedPagesMenuItem) => (
							<Menu position='bottom-end' withinPortal>
								<Menu.Target>
									<ActionIcon variant='light' size='lg' aria-label='More actions'>
										⋮
									</ActionIcon>
								</Menu.Target>
								<Menu.Dropdown>
									{embeddedPagesMenuItem}
									{bookingPageUrl && (
										<Menu.Item
											component='a'
											href={bookingPageUrl}
											target='_blank'
											rel='noopener noreferrer'
										>
											Visit booking page
										</Menu.Item>
									)}
									<Menu.Item onClick={() => setShowAddTravelerModal(true)}>
										Add traveler to check-in
									</Menu.Item>
									<Menu.Item onClick={handleViewMessageHistory}>
										View message history
									</Menu.Item>
								</Menu.Dropdown>
							</Menu>
						)}
					</CheckInEmbeddedPages>
				</Group>

				<Divider mt='xl' mb='xs' />

				<Button variant='subtle' color='blue' onClick={toggleNotes}>
					{showNotes
						? 'Hide Notes'
						: checkInNotes?.length > 0
							? 'Show Notes'
							: 'Add Notes'}
				</Button>

				{showNotes &&
					checkInNotes.map((note, idx) => {
						const isAuthor =
							note.createdByUser?.id === currentUser?.authUser?.id
						const canDeleteNote = isAuthor || isOrgAdmin
						const isEditing = noteMode === idx

						const noteId = getNoteId(note)

						return (
							<Card key={noteId || idx} shadow='sm' mt='sm' withBorder>
								{isEditing ? (
									<>
										<Textarea
											value={noteDraftText}
											onChange={(e) => setNoteDraftText(e.target.value)}
											minRows={3}
											mb='xs'
										/>
										<Group spacing='xs'>
											<Button
												size='xs'
												onClick={() => handleSaveNote()}
												loading={isSavingNote}
												disabled={!noteDraftText.trim() || isSavingNote}
											>
												Save
											</Button>
											<Button
												size='xs'
												variant='light'
												color='gray'
												onClick={handleCancelNote}
												disabled={isSavingNote}
											>
												Cancel
											</Button>
										</Group>
									</>
								) : (
									<Text>{note.note}</Text>
								)}
								<Group position='apart'>
									<Text size='sm' color='dimmed'>
										{note.createdByUser?.name || 'Unknown'} on{' '}
										{new Date(note.createdAtDateTime).toLocaleString()}
									</Text>
									{canDeleteNote && !isEditing && (
										<Group spacing='xs'>
											{isAuthor && (
												<Button
													size='xs'
													variant='light'
													disabled={isSavingNote}
													onClick={() => {
														setNoteMode(idx)
														setNoteDraftText(note.note)
													}}
												>
													Edit
												</Button>
											)}
											<Button
												size='xs'
												variant='subtle'
												color='red'
												loading={isSavingNote}
												disabled={isSavingNote}
												onClick={() => handleDeleteNote(idx)}
											>
												Delete
											</Button>
										</Group>
									)}
								</Group>
							</Card>
						)
					})}

				{showNotes && (
					<Box mt='md'>
						{noteMode !== 'add' ? (
							<Button
								onClick={() => {
									setNoteMode('add')
									setNoteDraftText('')
								}}
								disabled={isSavingNote}
							>
								Add Note
							</Button>
						) : (
							<>
								<Textarea
									value={noteDraftText}
									onChange={(e) => setNoteDraftText(e.target.value)}
									placeholder='Add a new note here...'
									minRows={4}
								/>
								<Group mt='xs' spacing='xs'>
									<Button
										onClick={() => handleSaveNote()}
										loading={isSavingNote}
										disabled={!noteDraftText.trim() || isSavingNote}
									>
										Save Note
									</Button>
									<Button
										variant='light'
										color='gray'
										onClick={handleCancelNote}
										disabled={isSavingNote}
									>
										Cancel
									</Button>
								</Group>
							</>
						)}
					</Box>
				)}
			</Box>

			<Modal
				opened={showCheckInDetailsModal}
				onClose={() => setShowCheckInDetailsModal(false)}
				title='Check-In Details'
				centered
			>
				<Text>
					<strong>Check-In Date Time:</strong>{' '}
					{formatActualCheckInDateTime(activeCheckIn.actualCheckInDateTime)}
				</Text>
				<Text mt='xs'>
					<strong>Staff Member:</strong>{' '}
					{activeCheckIn.checkedInBy?.name || 'N/A'}
				</Text>
				{isGroupMode && activeCheckIn.groupLeaderId != null && (
					<Text mt='xs'>
						<strong>Group Leader:</strong> {groupLeaderName || 'N/A'}
					</Text>
				)}

				<Divider my='md' />

				<Text fw={600} mb='xs'>
					{checkInDetailsSignatureLabel}
				</Text>
				{checkInDetailsSignature ? (
					<Box
						style={{
							border: '1px solid #ced4da',
							borderRadius: 8,
							backgroundColor: '#ffffff',
							padding: '0.5rem',
						}}
					>
						<img
							src={checkInDetailsSignature}
							alt='Check-in signature'
							style={{
								display: 'block',
								width: '100%',
								maxHeight: 220,
								objectFit: 'contain',
							}}
						/>
						<Text
							size='xs'
							color='dimmed'
							mt='xs'
							style={{ textAlign: 'right' }}
						>
							{formatSignatureDateTime(checkInDetailsSignatureDateTime)
								? `Signed on ${formatSignatureDateTime(
										checkInDetailsSignatureDateTime,
									)}`
								: 'Signature timestamp unavailable'}
						</Text>
					</Box>
				) : (
					<Text size='sm' color='dimmed'>
						No signature captured.
					</Text>
				)}
			</Modal>

			<Modal
				opened={showPurchasesModal}
				onClose={() => setShowPurchasesModal(false)}
				title='On-site purchases'
				centered
				size='lg'
			>
				{onSitePurchasedItems.length > 0 ? (
					<Box>
						{onSitePurchasedItems.map((item, idx) => (
							<Card
								key={
									item.bookingItemId ||
									item.orderId ||
									item.transactionId ||
									`${item.id}-${idx}`
								}
								withBorder
								radius='md'
								p='md'
								mb='sm'
							>
								<Group position='apart' align='flex-start' noWrap>
									<Box>
										<Text fw={600}>{item.name || 'Purchased item'}</Text>
										{item.description ? (
											<Text size='sm' color='dimmed' mt={2}>
												{item.description}
											</Text>
										) : null}
									</Box>
									<Group spacing='xs' noWrap>
										<Button
											size='xs'
											variant='subtle'
											onClick={() => openOnSitePurchaseDetail(item, idx)}
										>
											View purchase detail
										</Button>
									</Group>
								</Group>

								<Divider my='sm' />

								<Grid gutter='xs'>
									<Grid.Col xs={12} sm={6}>
										<Text size='xs' color='dimmed'>
											Method
										</Text>
										<Text size='sm'>
											{item.paymentMethod || 'Not recorded'}
										</Text>
									</Grid.Col>
									<Grid.Col xs={12} sm={6}>
										<Text size='xs' color='dimmed'>
											Amount paid
										</Text>
										<Text size='sm'>{formatPurchaseAmount(item)}</Text>
									</Grid.Col>
									<Grid.Col xs={12} sm={6}>
										<Text size='xs' color='dimmed'>
											Status
										</Text>
										<Text size='sm'>
											{item.paymentStatus || 'Not recorded'}
										</Text>
									</Grid.Col>
									<Grid.Col xs={12} sm={6}>
										<Text size='xs' color='dimmed'>
											Purchased
										</Text>
										<Text size='sm'>
											{formatPurchaseDateTime(item.addedAt)}
										</Text>
									</Grid.Col>
								</Grid>

								{item.orderId || item.transactionId ? (
									<Text size='xs' color='dimmed' mt='sm'>
										{item.orderId ? `Order ${item.orderId}` : ''}
										{item.orderId && item.transactionId ? ' · ' : ''}
										{item.transactionId
											? `Transaction ${item.transactionId}`
											: ''}
									</Text>
								) : null}
							</Card>
						))}
					</Box>
				) : (
					<Text size='sm' color='dimmed'>
						No on-site purchases recorded for this check-in.
					</Text>
				)}
			</Modal>

			<Modal
				opened={showAddTravelerModal}
				onClose={() => setShowAddTravelerModal(false)}
				title='Add traveler to check-in'
				centered
				size='lg'
			>
				<Text size='sm' color='dimmed' mb='sm'>
					Search by name, email, phone, or booking number.
				</Text>

				<TextInput
					placeholder='Search travelers...'
					value={travelerSearchTerm}
					onChange={(event) => setTravelerSearchTerm(event.target.value)}
					autoFocus
				/>

				{travelerSearchError && (
					<Text color='red' size='sm' mt='sm'>
						{travelerSearchError}
					</Text>
				)}

				{travelerSearchTerm.trim().length > 0 &&
					travelerSearchTerm.trim().length < 3 && (
						<Text color='dimmed' size='sm' mt='sm'>
							Enter at least 3 characters to search.
						</Text>
					)}

				{isTravelerSearchLoading && (
					<Group mt='sm' spacing='xs'>
						<Loader size='xs' />
						<Text size='sm' color='dimmed'>
							Searching travelers...
						</Text>
					</Group>
				)}

				{!isTravelerSearchLoading &&
					travelerSearchTerm.trim().length >= 3 &&
					travelerSearchResults.length === 0 && (
						<Text color='dimmed' size='sm' mt='sm'>
							No matching travelers found.
						</Text>
					)}

				{travelerSearchResults.length > 0 && (
					<Box mt='sm' style={{ maxHeight: 340, overflowY: 'auto' }}>
						{travelerSearchResults.map((traveler) => {
							const blockers = getTravelerAddBlockers(traveler)
							const canAdd = blockers.length === 0
							const isAddingThisTraveler =
								String(addingTravelerId) === String(traveler.id)

							return (
								<Card key={traveler.id} withBorder radius='md' p='sm' mb='xs'>
									<Group position='apart' align='flex-start' noWrap>
										<Box style={{ flex: 1, minWidth: 0 }}>
											<Text fw={600} lineClamp={1}>
												{traveler.travelerName || 'Unnamed traveler'}
											</Text>
											<Text size='sm' color='dimmed' lineClamp={1}>
												Email: {traveler.travelerEmail || 'N/A'}
											</Text>
											<Text size='sm' color='dimmed' lineClamp={1}>
												Phone: {traveler.travelerPhone || 'N/A'}
											</Text>
											<Text size='sm' color='dimmed' lineClamp={1}>
												Booking: {traveler.bookingNumber || 'N/A'}
											</Text>
											{!canAdd ? (
												<Text size='xs' color='red' mt={4}>
													Cannot add: {blockers.join(' • ')}
												</Text>
											) : (
												<Text size='xs' color='green' mt={4}>
													Eligible to add
												</Text>
											)}
										</Box>
										<Button
											size='xs'
											disabled={!canAdd || addingTravelerId !== null}
											loading={isAddingThisTraveler}
											onClick={() => handleAddTravelerToGroup(traveler)}
										>
											Add
										</Button>
									</Group>
								</Card>
							)
						})}
					</Box>
				)}
			</Modal>

			<Modal
				opened={
					showMissingFieldsModal &&
					String(missingFieldPrompt.checkInId) === String(activeCheckIn?.id)
				}
				onClose={() => {
					if (!hasRequiredMissingFieldPrompt) {
						handleMissingFieldsSkip()
					}
				}}
				title='Fill in missing fields'
				centered
				closeOnEscape={!hasRequiredMissingFieldPrompt && !isSavingMissingFields}
				closeOnClickOutside={
					!hasRequiredMissingFieldPrompt && !isSavingMissingFields
				}
				withCloseButton={
					!hasRequiredMissingFieldPrompt && !isSavingMissingFields
				}
			>
				<form onSubmit={handleMissingFieldsSubmit}>
					<Text size='sm' color='dimmed' mb='md'>
						Update this traveler with any missing details below.
					</Text>

					{missingFieldPrompt.fieldKeys.map((fieldKey, index) => {
						const fieldDefinition = getCollectFieldDefinition(fieldKey)
						if (!fieldDefinition) return null
						const isRequiredField =
							missingFieldPrompt.requiredFieldKeys.includes(fieldKey)
						const label = `${fieldDefinition.label}${isRequiredField ? ' *' : ''}`
						const fieldError = missingFieldFormErrors[fieldKey] || null

						if (fieldKey === 'travelerPhone') {
							return (
								<Input.Wrapper
									key={fieldKey}
									label={label}
									error={fieldError}
									mb='sm'
								>
									<PhoneInput
										defaultCountry='US'
										international
										countryCallingCodeEditable={false}
										placeholder={fieldDefinition.placeholder}
										value={missingFieldFormValues[fieldKey] || undefined}
										onChange={(value) =>
											updateMissingFieldValue(fieldKey, value || '')
										}
										className='phone-input'
										autoFocus={index === 0}
									/>
								</Input.Wrapper>
							)
						}

						return (
							<TextInput
								key={fieldKey}
								label={label}
								placeholder={fieldDefinition.placeholder}
								value={missingFieldFormValues[fieldKey] || ''}
								onChange={(event) =>
									updateMissingFieldValue(fieldKey, event.target.value)
								}
								error={fieldError}
								mb='sm'
								autoFocus={index === 0}
							/>
						)
					})}

					<Group mt='md' position='apart'>
						<Button
							type='button'
							variant='light'
							onClick={
								hasRequiredMissingFieldPrompt
									? handleMissingFieldsBack
									: handleMissingFieldsSkip
							}
							disabled={isSavingMissingFields}
						>
							{hasRequiredMissingFieldPrompt ? 'Back' : 'Skip'}
						</Button>
						<Button
							type='submit'
							disabled={!isMissingFieldDoneEnabled}
							loading={isSavingMissingFields}
						>
							Done
						</Button>
					</Group>
				</form>
			</Modal>

			<Modal
				opened={showBackWarningModal}
				onClose={() => setShowBackWarningModal(false)}
				title='Are you sure?'
				centered
			>
				<Text>
					Are you sure you want to go back without finalizing the check-in?
					At least one item has been issued, but the check-in is still incomplete.
				</Text>
				<Group mt='md'>
					<Button
						variant='light'
						onClick={() => setShowBackWarningModal(false)}
					>
						Cancel
					</Button>
					<Button
						disabled={!canContinueBack}
						onClick={() => navigate(experiencePath)}
						rightIcon={
							showBackWarningModal && !canContinueBack ? (
								<svg
									width='18'
									height='18'
									viewBox='0 0 20 20'
									fill='none'
									stroke='#228be6'
									strokeWidth='2.5'
									role='presentation'
									aria-hidden='true'
									focusable='false'
									style={{ transform: 'rotate(-90deg)' }}
								>
									<circle cx='10' cy='10' r='8' opacity='0.2' />
									<circle
										cx='10'
										cy='10'
										r='8'
										pathLength='100'
										strokeDasharray='100'
										strokeDashoffset='100'
										strokeLinecap='round'
										style={{ animation: 'checkin-back-countdown 2s linear forwards' }}
									/>
								</svg>
							) : null
						}
					>
						Continue
					</Button>
				</Group>
			</Modal>

			<Modal
				opened={showWarningModal}
				onClose={() => {
					setShowWarningModal(false)
					setNoteMode(null)
					setNoteDraftText('')
				}}
				title='ARE YOU SURE?'
				centered
			>
				<Text mb='sm'>There are items you did not issue to the traveler.</Text>

				<Textarea
					value={noteDraftText}
					onChange={(e) => setNoteDraftText(e.target.value)}
					placeholder='Add an optional note...'
					minRows={4}
				/>

				<Group mt='md'>
					<Button
						variant='light'
						onClick={() => {
							setShowWarningModal(false)
							setNoteMode(null)
							setNoteDraftText('')
						}}
					>
						Go Back
					</Button>

					<Button
						loading={isFinalizingCheckIn}
						disabled={isFinalizingCheckIn}
						onClick={async () => {
							setShowWarningModal(false)

							const pendingNoteText = noteDraftText
							setNoteDraftText('')
							setNoteMode(null)

							if (isGroupMode) {
								await runFinalizeWithLock(() =>
									handleGroupPrimaryAction({
										pendingNoteText,
									}),
								)
								return
							}

							await runFinalizeWithLock(() =>
								handleIndividualPrimaryAction({
									pendingNoteText,
								}),
							)
						}}
					>
						{warningConfirmLabel}
					</Button>
				</Group>
			</Modal>

			<Modal
				opened={showCriticalModal}
				onClose={() => setShowCriticalModal(false)}
				title='HELP REQUIRED'
				centered
			>
				<Text>
					Please contact a supervisor.
					<br />
					This traveler cannot be checked-in without assistance.
				</Text>
				<Group mt='md'>
					<Button onClick={() => setShowCriticalModal(false)}>Go Back</Button>
				</Group>
			</Modal>

			<Modal
				opened={showSignatureModal}
				onClose={() => {
					if (!isSavingSignature) closeSignatureModal()
				}}
				title='Traveler Signature Required'
				centered
				size='lg'
				closeOnEscape={!isSavingSignature}
				closeOnClickOutside={!isSavingSignature}
			>
				<Text mb='sm'>{signaturePromptText}</Text>

				<Box
					style={{
						border: '1px solid #ced4da',
						borderRadius: 8,
						overflow: 'hidden',
					}}
				>
					<canvas
						ref={signatureCanvasRef}
						width={640}
						height={220}
						style={{
							display: 'block',
							width: '100%',
							height: 220,
							backgroundColor: '#ffffff',
							touchAction: 'none',
							cursor: 'crosshair',
						}}
						onPointerDown={handleSignaturePointerDown}
						onPointerMove={handleSignaturePointerMove}
						onPointerUp={handleSignaturePointerUp}
						onPointerLeave={handleSignaturePointerUp}
					/>
				</Box>

				<Group mt='md' position='apart'>
					<Button
						variant='subtle'
						onClick={clearSignatureCanvas}
						disabled={isSavingSignature}
					>
						Clear Signature
					</Button>

					<Group spacing='xs'>
						<Button
							variant='light'
							onClick={closeSignatureModal}
							disabled={isSavingSignature}
						>
							Cancel
						</Button>
						<Button
							onClick={handleConfirmSignature}
							disabled={!signatureHasInput}
							loading={isSavingSignature}
						>
							Save Signature
						</Button>
					</Group>
				</Group>
			</Modal>

			<PurchasedItemsModal
				show={showAddItemModal}
				onClose={() => setShowAddItemModal(false)}
				experienceSlug={experienceSlug}
				checkInId={activeCheckIn?.id}
				currentItems={purchasedItems}
				onCheckoutCreated={refreshActiveCheckInData}
			/>
		</Box>
	)
}

export default CheckInDetailPage
